import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getAnimalHistoricEventPage } from "#/redux/resource/AnimalHistoricEvent";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils";
import { getErrorMessage } from "#/utils/APIUtils";

// Fetch aggregated milk record data with animal information
export const fetchMilkControlAggregateById = createAsyncThunk(
  "milkControls/aggregateById",
  async (id, { rejectWithValue }) => {
    try {
      // Step 1: Fetch milk control by ID
      const milkControlResponse = await ApiClient.get(
        `/api/dog/mleko/mlecnostKontrola/${id}`
      );

      const milkControl = milkControlResponse.data;

      // Step 2: Fetch animal data from vwsamicemlecnost using molzaId
      if (milkControl.molzaId) {
        const animalFilter = new ApiFilter().equal(
          "molza.id",
          milkControl.molzaId
        );

        const animalResponse = await ApiClient.get(
          "/api/zival/vwsamicemlecnost/page",
          undefined,
          { page: 0, size: 1 },
          animalFilter,
          null
        );

        const animalData = animalResponse.data?.content?.[0]?.zival;

        // Return aggregated data
        return {
          ...milkControl,
          zival: animalData,
        };
      }

      return milkControl;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Fetch milk controls with animal data for filtering
export const fetchMilkControlsWithAnimalData = createAsyncThunk(
  "milkControls/listWithAnimalData",
  async ({ animalId, page }, { rejectWithValue, dispatch }) => {
    if (!animalId) {
      throw new Error("animalId is required");
    }

    try {
      // Step 1: Fetch animal historic events filtered by animalId and milk control type
      const historicEventsFilter = new ApiFilter()
        .equal("zival.id", animalId)
        .like("dogodek", encodeURIComponent("MLECNOST_KONTROLA"));

      const animalEventsResponse = await dispatch(
        getAnimalHistoricEventPage({
          page,
          filter: historicEventsFilter,
          sort: "createdOn,DESC",
        })
      ).unwrap();

      // If no events found, return empty result
      if (!animalEventsResponse?.content?.length) {
        return {
          content: [],
          total: 0,
        };
      }

      // Step 2: Extract milk control IDs from zapisId
      const milkControlIds = animalEventsResponse.content
        .map((event) => event.zapisId)
        .filter(Boolean);

      // Step 3: Fetch milk controls by IDs (if IN filter is supported)
      const milkControlsFilter = new ApiFilter();
      if (milkControlIds.length === 1) {
        milkControlsFilter.equal("id", milkControlIds?.[0]);
      } else {
        milkControlsFilter.in("id", milkControlIds);
      }

      const milkControlsResponse = await ApiClient.get(
        "/api/dog/mleko/mlecnostKontrola/page",
        null,
        page,
        milkControlsFilter,
        null
      );

      const milkControls = milkControlsResponse.data.content || [];

      // Step 4: Create a map of milkControlId -> animal data
      const animalDataMap = {};
      animalEventsResponse.content.forEach((event) => {
        if (event.zapisId && event.zival) {
          animalDataMap[event.zapisId] = event.zival;
        }
      });

      // Step 5: Join milk controls with animal data
      const milkControlsWithAnimalData = milkControls.map((milkControl) => ({
        ...milkControl,
        zival: animalDataMap[milkControl.id],
      }));

      return {
        ...milkControlsResponse.data,
        content: milkControlsWithAnimalData,
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Fetch animals that are eligible for milk records entry
export const fetchAnimalsWithMolza = createAsyncThunk(
  "milkControls/animalsWithMolza",
  async ({ page }, { rejectWithValue, dispatch }) => {
    try {
      // 1. Fetch molza records that don't have konecMolzeDatum
      const molzaFilter = new ApiFilter().isNull("konecMolzeDatum");

      const molzaResponse = await ApiClient.get(
        "/api/dog/mleko/molza/page",
        undefined,
        page,
        molzaFilter,
        "sort=zacetekMolzeDatum,desc"
      );

      // No valid molza records found
      if (molzaResponse.data.content.length === 0) {
        return [];
      }

      // 2. enrich with animal data using historicEvents API
      const historicEventsFilter = new ApiFilter().in(
        "zapisId",
        molzaResponse.data.content.map((molza) => molza.id)
      );

      const animalEventsResponse = await dispatch(
        getAnimalHistoricEventPage({
          filter: historicEventsFilter,
        })
      ).unwrap();

      // If no events found, return empty array
      if (!animalEventsResponse?.content?.length) {
        return [];
      }

      // Step 2: Extract unique animals from events
      const uniqueAnimalsMap = new Map();
      animalEventsResponse.content.forEach((event) => {
        if (event.zival && !uniqueAnimalsMap.has(event.zival.id)) {
          uniqueAnimalsMap.set(event.zival.id, event.zival);
        }
      });

      // Step 3: Convert map to array
      const animalsWithMolza = Array.from(uniqueAnimalsMap.values());

      return animalsWithMolza;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchMilkControls = createAsyncThunk(
  "milkControls/list",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/dog/mleko/mlecnostKontrola/page",
        undefined,
        page,
        filter,
        sort
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createMilkControl = createAsyncThunk(
  "milkControls/create",
  async (milkControlData, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(
        "/api/dog/mleko/mlecnostKontrola",
        milkControlData
      );

      return response.data.output;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateMilkControl = createAsyncThunk(
  "milkControls/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      await ApiClient.put(`/api/dog/mleko/mlecnostKontrola/${id}`, data);

      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteMilkControl = createAsyncThunk(
  "milkControls/delete",
  async ({ id, version }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.delete(
        `/api/dog/mleko/mlecnostKontrola/${id}?version=${version}`
      );

      return response.data.output;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getMilkControlById = createAsyncThunk(
  "milkControls/details",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `/api/dog/mleko/mlecnostKontrola/${id}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Fetch latest milk control for an animal
export const checkMilkControlExistsForDate = createAsyncThunk(
  "milkControls/latestMilkControl",
  async ({ molzaId, datumKontrole }, { rejectWithValue }) => {
    if (!molzaId) {
      throw new Error("molzaId is required");
    }

    try {
      // Filter by animal and exact control date
      const filter = new ApiFilter()
        .equal("molzaId", molzaId)
        .equal("datumKontrole", datumKontrole.toISOString().split("T")[0]);

      const response = await ApiClient.get(
        "/api/dog/mleko/mlecnostKontrola/page",
        undefined,
        { page: 0, size: 1 }, // Only need to check if at least one exists
        filter,
        "sort=datumKontrole,desc"
      );

      return response.data?.content?.length > 0;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Fetch count of milk controls for a specific lactation (molzaId)
export const fetchMilkControlCountByMolzaId = createAsyncThunk(
  "milkControls/countByMolzaId",
  async (molzaId, { rejectWithValue }) => {
    if (!molzaId) {
      throw new Error("molzaId is required");
    }

    try {
      // Filter by molzaId
      const filter = new ApiFilter().equal("molzaId", molzaId);

      const response = await ApiClient.get(
        "/api/dog/mleko/mlecnostKontrola/page",
        undefined,
        { page: 0, size: 1 }, // Only need the count
        filter,
        null
      );

      return response.data?.totalElements || 0;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Fetch all milk controls for a specific lactation (molzaId)
export const fetchMilkControlsForLactation = createAsyncThunk(
  "milkControls/controlsForLactation",
  async (molzaId, { rejectWithValue }) => {
    if (!molzaId) {
      throw new Error("molzaId is required");
    }

    try {
      // Filter by molzaId and sort by datumKontrole ASC
      const filter = new ApiFilter().equal("molzaId", molzaId);

      const response = await ApiClient.get(
        "/api/dog/mleko/mlecnostKontrola/page",
        undefined,
        { page: 0, size: 1000 }, // Get all controls for the lactation
        filter,
        "sort=zapStKontrole,desc"
      );

      return response.data?.content || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// Get next zapStKontrole for a given molzaId
export const getNextZapStKontroleByMolzaId = createAsyncThunk(
  "milkControls/nextZapStKontrole",
  async (molzaId, { rejectWithValue }) => {
    if (!molzaId) {
      throw new Error("molzaId is required");
    }

    try {
      // Filter by molzaId and sort by zapStKontrole DESC to get the highest number
      const filter = new ApiFilter().equal("molzaId", molzaId);

      const response = await ApiClient.get(
        "/api/dog/mleko/mlecnostKontrola/page",
        undefined,
        { page: 0, size: 1 }, // Only need the latest record
        filter,
        "sort=zapStKontrole,desc"
      );

      const latestRecord = response.data?.content?.[0];

      // If no record exists, return 1, otherwise return latest + 1
      return latestRecord ? latestRecord.zapStKontrole + 1 : 1;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const thunks = [
  fetchMilkControlsWithAnimalData,
  fetchAnimalsWithMolza,
  fetchMilkControls,
  createMilkControl,
  updateMilkControl,
  deleteMilkControl,
  getMilkControlById,
  fetchMilkControlAggregateById,
  getNextZapStKontroleByMolzaId,
  fetchMilkControlsForLactation,
  fetchMilkControlCountByMolzaId,
];

// Initial state
const initialState = {
  isLoading: false, // Single boolean loading state
  page: { content: [] },
  details: {},
  aggregateDetails: {}, // New state for aggregated data
  error: null,
  pageWithAnimalData: {
    content: [],
  },
  animalsWithMolza: [],
  milkControlExists: false,
  nextZapStKontrole: null,
  lactationMilkControls: [], // Store all controls for current lactation
  latestLactationMilkControl: null,
};

// Create slice
const milkControlSlice = createSlice({
  name: "milkControls",
  initialState,
  reducers: {
    clearDetails: (state) => {
      state.details = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAggregateDetails: (state) => {
      state.aggregateDetails = {};
    },
    clearLactationMilkControls: (state) => {
      state.latestLactationMilkControl = null;
      state.lactationMilkControls = [];
    },
    clearNextZapStKontrole: (state) => {
      state.nextZapStKontrole = null;
    },
    clearPageWithAnimalData: (state) => {
      state.pageWithAnimalData = {
        content: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchMilkControlsWithAnimalData.fulfilled,
      (state, action) => {
        state.pageWithAnimalData = action.payload;
      }
    );

    builder.addCase(fetchAnimalsWithMolza.fulfilled, (state, action) => {
      state.animalsWithMolza = action.payload;
    });

    builder.addCase(fetchMilkControls.fulfilled, (state, action) => {
      state.page = action.payload;
    });

    builder.addCase(updateMilkControl.fulfilled, (state, action) => {
      state.details = action.payload;
    });
    builder.addCase(getMilkControlById.fulfilled, (state, action) => {
      state.details = action.payload;
    });

    builder.addCase(
      fetchMilkControlAggregateById.fulfilled,
      (state, action) => {
        state.aggregateDetails = action.payload;
      }
    );

    builder.addCase(
      getNextZapStKontroleByMolzaId.fulfilled,
      (state, action) => {
        state.nextZapStKontrole = action.payload;
      }
    );

    builder.addCase(
      fetchMilkControlsForLactation.fulfilled,
      (state, action) => {
        state.lactationMilkControls = action.payload;
        state.latestLactationMilkControl = action.payload?.[0];
      }
    );

    handleLoadingAndErrorState(builder, thunks);
  },
});

export const {
  clearDetails,
  clearError,
  clearAggregateDetails,
  clearLactationMilkControls,
  clearNextZapStKontrole,
  clearPageWithAnimalData,
} = milkControlSlice.actions;

export default milkControlSlice.reducer;
